import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { api } from "@/api/client";
import { router } from "@/router";

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  branch_ids: string[];
  company_id: string | null;
  invited_by_id: string | null;
  is_email_verified: boolean;
  theme: string | null;
  phone?: string;
}

export interface Tokens {
  access_token: string;
  refresh_token: string;
}

const STORAGE_KEY = "vertex_auth";

function loadFromStorage(): { user: User | null; tokens: Tokens | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        user: parsed.user || null,
        tokens: parsed.tokens || null,
      };
    }
  } catch {
    // ignore parse errors
  }
  return { user: null, tokens: null };
}

function saveToStorage(user: User | null, tokens: Tokens | null) {
  if (user && tokens) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ user, tokens }));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export const useAuthStore = defineStore("auth", () => {
  const stored = loadFromStorage();
  const user = ref<User | null>(stored.user);
  const tokens = ref<Tokens | null>(stored.tokens);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!tokens.value?.access_token);
  const isAdmin = computed(() => user.value?.role === "admin");
  const isCompanyAdmin = computed(() => user.value?.role === "company_admin");
  const isManager = computed(() => user.value?.role === "manager");
  const canManageUsers = computed(() =>
    ["admin", "company_admin", "manager"].includes(user.value?.role || "")
  );
  const canManageBranches = computed(() =>
    ["admin", "company_admin"].includes(user.value?.role || "")
  );

  async function fetchUser() {
    if (!tokens.value?.access_token) return;
    try {
      const { data } = await api.get<User>("/users/me");
      user.value = data;
      saveToStorage(user.value, tokens.value);
    } catch {
      // If fetching user fails, clear auth
      logout();
    }
  }

  async function login(email: string, password: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const { data } = await api.post<{
        access_token: string;
        refresh_token: string;
        token_type: string;
        user: User;
      }>("/auth/login", {
        email,
        password,
      });
      tokens.value = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      };
      user.value = data.user;
      saveToStorage(user.value, tokens.value);
    } catch (err: any) {
      error.value = err.response?.data?.detail || "Login failed";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function registerCompany(payload: {
    company_name: string;
    email: string;
    password: string;
    full_name: string;
    phone?: string;
  }) {
    isLoading.value = true;
    error.value = null;
    try {
      const { data } = await api.post<{
        access_token: string;
        refresh_token: string;
        token_type: string;
        user: User;
      }>("/auth/register", payload);
      tokens.value = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      };
      user.value = data.user;
      saveToStorage(user.value, tokens.value);
    } catch (err: any) {
      error.value = err.response?.data?.detail || "Registration failed";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function registerWithInvite(payload: {
    token: string;
    password: string;
    full_name: string;
    phone?: string;
  }) {
    isLoading.value = true;
    error.value = null;
    try {
      const { data } = await api.post<{
        access_token: string;
        refresh_token: string;
        token_type: string;
        user: User;
      }>("/auth/register/invite", payload);
      tokens.value = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      };
      user.value = data.user;
      saveToStorage(user.value, tokens.value);
    } catch (err: any) {
      error.value = err.response?.data?.detail || "Registration failed";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function resendVerification(email: string) {
    isLoading.value = true;
    error.value = null;
    try {
      await api.post("/auth/resend-verification", { email });
    } catch (err: any) {
      error.value = err.response?.data?.detail || "Failed to resend verification email";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateProfile(payload: {
    full_name?: string;
    email?: string;
    phone?: string;
    theme?: string;
  }) {
    isLoading.value = true;
    error.value = null;
    try {
      const { data } = await api.patch<User>(`/users/${user.value?.id}`, payload);
      user.value = data;
      saveToStorage(user.value, tokens.value);
    } catch (err: any) {
      error.value = err.response?.data?.detail || "Failed to update profile";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function changePassword(current_password: string, new_password: string) {
    isLoading.value = true;
    error.value = null;
    try {
      await api.post("/auth/change-password", { current_password, new_password });
    } catch (err: any) {
      error.value = err.response?.data?.detail || "Failed to change password";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function refreshAccessToken(): Promise<boolean> {
    if (!tokens.value?.refresh_token) return false;
    try {
      const { data } = await api.post<Tokens>("/auth/refresh", {
        refresh_token: tokens.value.refresh_token,
      });
      tokens.value = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      };
      saveToStorage(user.value, tokens.value);
      return true;
    } catch {
      logout();
      return false;
    }
  }

  function logout() {
    user.value = null;
    tokens.value = null;
    localStorage.removeItem(STORAGE_KEY);
    router.push({ name: "login" });
  }

  return {
    user,
    tokens,
    isLoading,
    error,
    isAuthenticated,
    isAdmin,
    isCompanyAdmin,
    isManager,
    canManageUsers,
    canManageBranches,
    login,
    logout,
    fetchUser,
    refreshAccessToken,
    registerCompany,
    registerWithInvite,
    resendVerification,
    updateProfile,
    changePassword,
  };
});
